import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormButtons,
  FormHeader,
  Input,
  NormalizedException,
  errorStaticText,
  useCountdown,
  useRsaContext,
} from "@songtradr-sso/shared/frontend";
import {
  A,
  Alert,
  Body,
  Button,
  ButtonLoadingIcon,
  Heading,
} from "@songtradr/design-language";
import { PageContainer } from "../../components/page-container/PageContainer";
import {
  FormFields,
  confirmEmailViaCode,
  resendVerificationCode,
  routes,
  schema,
  text,
} from "./EmailConfirmation.Parts";

export const EmailConfirmation = () => {
  const navigate = useNavigate();
  const { rsaEncrypt } = useRsaContext();

  const formMethods = useForm<FormFields>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      [text.shared.inputs.email.id]: sessionStorage.getItem("email") ?? "",
    },
  });

  const {
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = formMethods;

  const email = watch(text.shared.inputs.email.id);
  const errorMessage = !email ? text.emailError : errors.root?.message;

  const [resending, resendIn, startResendCountdown] = useCountdown();

  const resendCode = async () => {
    try {
      if (email) {
        startResendCountdown();
        await resendVerificationCode(email);
      } else {
        setError("root", { message: errorStaticText.default });
      }
    } catch (err) {
      setError("root", { message: new NormalizedException(err).message });
    }
  };

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const rsaEncryptedRequest = await rsaEncrypt(
        JSON.stringify({
          email: data.email,
          verificationCode: data.code,
        }),
      );
      const verified = await confirmEmailViaCode(rsaEncryptedRequest);
      if (verified) navigate(routes.signIn);
    } catch (err) {
      setError("root", { message: new NormalizedException(err).message });
    }
  };

  return (
    <PageContainer floatTop backButton>
      <FormHeader>
        <Heading variant="h3-strong">{text.title}</Heading>
        <Body>{text.description}</Body>
      </FormHeader>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <Heading variant="section">{text.inputLabel}</Heading>
        <Input
          id={text.shared.inputs.verificationCode.id}
          label={text.shared.inputs.verificationCode.label}
        />
        <Alert message={errorMessage} dismissible />
        <FormButtons $justify="space-between">
          <A $disabled={resending} onClick={resendCode}>
            {text.buttons.resend}
            {resending && ` (${resendIn})`}
          </A>
          <Button
            $variant="important"
            type="submit"
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? <ButtonLoadingIcon /> : text.buttons.submit}
          </Button>
        </FormButtons>
      </Form>
    </PageContainer>
  );
};
